import React, { forwardRef } from 'react';
import type { ReactNode } from 'react';
import { Alert, AlertTitle, IconButton } from '@mui/material';
import Stack from '@mui/material/Stack';

interface CustomAlertProps {
  id: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
  variant?: 'filled' | 'outlined' | 'standard';
  children?: ReactNode;
  title?: string;
  message: string | ReactNode;
  onClose?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}

// eslint-disable-next-line react/display-name
const CustomAlert = forwardRef<HTMLDivElement, CustomAlertProps>(
  (
    {
      id,
      severity = 'info',
      title,
      message,
      onClose,
      action,
      icon,
      className,
      variant = 'standard',
      children,
    },
    ref
  ) => {
    const getSnackbarStyles = () => {
      switch (severity) {
      case 'success':
        return { backgroundColor: '#E4F0DD' };
      case 'error':
        return { backgroundColor: '#F5D8D8' };
      case 'warning':
        return { background: '#FFF8E7' };
      case 'info':
        return { backgroundColor: '#EBE2F2' };
      default:
        return {};
      }
    };
    return (
      <Stack sx={{ width: '100%' }} spacing={2}>
        <Alert
          id={id}
          ref={ref}
          severity={severity}
          icon={icon}
          variant={variant}
          action={
            action ||
            (onClose && (
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={onClose}
              />
            ))
          }
          className={className}
          sx={{
            ...getSnackbarStyles(),
          }}
        >
          {title && <AlertTitle>{title}</AlertTitle>}
          {message}
          {children}
        </Alert>
      </Stack>
    );
  }
);

export default CustomAlert;
